import React, { useState } from 'react';
import './OdooSync.css';
import { Modal, Progress, message } from 'antd';
import {
  SyncOutlined,
  SearchOutlined,
  CalendarOutlined,
  ReloadOutlined,
  DatabaseOutlined,
  ShoppingOutlined,
  UnorderedListOutlined,
  UserOutlined,
  ExportOutlined,
  FileExcelOutlined,
  DownloadOutlined,
  CarOutlined,
  WarningOutlined,
  CheckSquareOutlined,
  AppstoreOutlined,
  ExclamationCircleOutlined,
  FileTextOutlined,
  InboxOutlined,
  RightOutlined,
  DownOutlined,
  CaretUpOutlined,
  CaretDownOutlined,
  SwapOutlined,
  DoubleLeftOutlined,
  DoubleRightOutlined,
  LeftOutlined,
  CheckCircleOutlined,
  CheckCircleFilled,
  CloseCircleFilled,
  CloseOutlined,
  LoadingOutlined,
  CloudUploadOutlined
} from '@ant-design/icons';
import { Calendar } from 'primereact/calendar';
import config from '../config';
import * as XLSX from 'xlsx';
import Cookies from 'js-cookie';

/* ───────────── Helpers ───────────── */

const getAuthHeaders = (includeJson = false) => {
  const token = Cookies.get('token') || localStorage.getItem('token');

  return {
    ...(includeJson ? { 'Content-Type': 'application/json' } : {}),
    ...(token ? { Authorization: `Bearer ${token}` } : {})
  };
};

const parseDate = (d) => {
  try {
    return d ? new Date(d) : new Date(0);
  } catch (e) {
    return new Date(0);
  }
};

// Merge multiple WH/OUT deliveries of one order into a single delivery
const getConsolidatedDelivery = (deliveries) => {
  const whOutDeliveries =
    deliveries?.filter((delivery) => {
      const isWHOut = delivery.name?.includes('WH/OUT');
      const isCustomerDelivery = delivery.destination === 'Partners/Customers';
      const hasProducts = delivery.products && delivery.products.length > 0;
      return (isWHOut || isCustomerDelivery) && hasProducts && !delivery.name?.includes('WH/IN');
    }) || [];

  if (whOutDeliveries.length === 0) return null;
  if (whOutDeliveries.length === 1) return whOutDeliveries[0];

  // Deduplicate products by product_id, keeping the entry from the latest delivery
  const productMap = new Map();
  whOutDeliveries.forEach((delivery) => {
    const deliveryDate = parseDate(delivery.scheduled_date);
    delivery.products?.forEach((product) => {
      const key = product.product_id?.toString() || `${product.barcode || ''}_${product.reference || ''}`;
      const existing = productMap.get(key);
      if (!existing || deliveryDate > existing.scheduled_date) {
        productMap.set(key, { product: { ...product }, scheduled_date: deliveryDate });
      }
    });
  });
  const consolidatedProducts = Array.from(productMap.values()).map((v) => v.product);

  const latestBy = (list) =>
    list.length ? list.reduce((best, cur) => (parseDate(cur.scheduled_date) > parseDate(best.scheduled_date) ? cur : best)) : null;

  const latestDelivery = latestBy(whOutDeliveries);
  const latestTrackingDelivery = latestBy(
    whOutDeliveries.filter((d) => d.carrier_tracking_ref && d.carrier_tracking_ref.toString().trim() !== '')
  );
  const latestCarrierDelivery = latestBy(whOutDeliveries.filter((d) => d.carrier && d.carrier.toString().trim() !== ''));

  return {
    id: latestDelivery.id,
    name: latestDelivery.name,
    destination: latestDelivery.destination,
    state: latestDelivery.state,
    carrier: latestCarrierDelivery?.carrier || latestDelivery.carrier || '',
    carrier_tracking_ref: latestTrackingDelivery?.carrier_tracking_ref || latestDelivery.carrier_tracking_ref || '',
    carrier_tracking_url: latestTrackingDelivery?.carrier_tracking_url || latestDelivery.carrier_tracking_url || '',
    products: consolidatedProducts,
    scheduled_date: latestDelivery.scheduled_date,
    origin: whOutDeliveries.map((d) => d.origin).filter(Boolean).join(', '),
    attachments: latestTrackingDelivery?.attachments || latestDelivery.attachments || []
  };
};

const getCarrierName = (order) => getConsolidatedDelivery(order.deliveries)?.carrier || '-';
const getTrackingId = (order) => getConsolidatedDelivery(order.deliveries)?.carrier_tracking_ref || '-';
const getDeliveryStatus = (order) => getConsolidatedDelivery(order.deliveries)?.state || '-';

const hasShipping = (order) => {
  const c = getConsolidatedDelivery(order.deliveries);
  return !!c && (c.carrier || '').toString().trim() !== '' && (c.carrier_tracking_ref || '').toString().trim() !== '';
};

const daysInMonth = (year, month) => new Date(year, month, 0).getDate();

const csvCell = (v) => `"${String(v ?? '').replace(/"/g, '""')}"`;

const getYesterdayDate = () => {
  const y = new Date();
  y.setDate(y.getDate() - 1);
  return { year: y.getFullYear(), month: y.getMonth() + 1, day: y.getDate() };
};

/* ───────────── Component ───────────── */

function OdooSync({ userName, darkTheme = false }) {
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [fetchProgress, setFetchProgress] = useState({
    currentBatch: 0, totalBatches: 0, ordersFetched: 0, totalOrders: 0, status: 'idle', activities: []
  });

  const [selectedDate, setSelectedDate] = useState(getYesterdayDate());
  const [dashboardStats, setDashboardStats] = useState({ totalOrders: 0, date: '' });
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedOrders, setSelectedOrders] = useState(new Set());
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(50);
  const [sortConfig, setSortConfig] = useState({ key: null, direction: 'asc' });
  const [expandedOrder, setExpandedOrder] = useState(null);
  const [pageInput, setPageInput] = useState('');
  const [hasFetched, setHasFetched] = useState(false);
  const [bulkInsertModal, setBulkInsertModal] = useState(false);
  const [insertLoading, setInsertLoading] = useState(false);
  const [insertProgress, setInsertProgress] = useState(0);
  const [packingScheduledDate, setPackingScheduledDate] = useState(new Date());
  const [exportProductsModal, setExportProductsModal] = useState(false);
  const [exportLoading, setExportLoading] = useState(false);
  const [exportProgress, setExportProgress] = useState(0);
  const [showOnlyWithShipping, setShowOnlyWithShipping] = useState(false);
  const [showOnlyMissingShipping, setShowOnlyMissingShipping] = useState(false);
  const [singleOrderSearch, setSingleOrderSearch] = useState('');
  const [searchMode, setSearchMode] = useState('date'); // 'date' | 'order' | 'multiple'
  const [multipleOrderSearch, setMultipleOrderSearch] = useState('');
  const [multipleOrdersLoading, setMultipleOrdersLoading] = useState(false);

  const resetView = () => {
    setCurrentPage(1);
    setSelectedOrders(new Set());
    setExpandedOrder(null);
    setPageInput('');
    setShowOnlyWithShipping(false);
    setShowOnlyMissingShipping(false);
  };

  /* ── Fetching ── */

  const fetchOrders = (year, month, day) => {
    setLoading(true);
    setError(null);
    setHasFetched(false);
    resetView();
    setFetchProgress({ currentBatch: 0, totalBatches: 0, ordersFetched: 0, totalOrders: 0, status: 'connecting', activities: [] });

    const eventSource = new EventSource(
      `${config.apiBaseUrl}/api/sale-orders/day/stream?year=${year}&month=${month}&day=${day}`
    );

    eventSource.addEventListener('progress', (event) => {
      try {
        const data = JSON.parse(event.data);
        setFetchProgress((prev) => {
          const activities = [...prev.activities];
          const item = { step: data.step, message: data.message, timestamp: new Date().toLocaleTimeString() };
          if (data.step === 'batch_complete') {
            if (!activities.some((a) => a.message === data.message)) activities.push(item);
          } else if (data.step !== 'complete' && data.step !== 'processing') {
            const idx = activities.findIndex((a) => a.step === data.step);
            if (idx >= 0) activities[idx] = item;
            else activities.push(item);
          }
          return {
            currentBatch: data.currentBatch || prev.currentBatch,
            totalBatches: data.totalBatches || prev.totalBatches,
            ordersFetched: data.ordersFetched || prev.ordersFetched,
            totalOrders: data.totalOrders || prev.totalOrders,
            status: data.step || prev.status,
            activities
          };
        });
      } catch (e) { /* ignore malformed progress events */ }
    });

    eventSource.addEventListener('complete', (event) => {
      try {
        const data = JSON.parse(event.data);
        setOrders(data.data || []);
        setDashboardStats({ totalOrders: data.totalOrders || 0, date: data.date || '' });
        setHasFetched(true);
        setFetchProgress((prev) => ({ ...prev, status: 'complete' }));
      } catch (e) { /* ignore */ }
      eventSource.close();
      setLoading(false);
    });

    // Single error handler: covers server-sent "error" events and dropped connections
    eventSource.addEventListener('error', (event) => {
      let msg = 'Connection lost or server error. Please try again.';
      try {
        if (event.data) msg = JSON.parse(event.data).message || msg;
      } catch (e) { /* keep default */ }
      setError(msg);
      setLoading(false);
      eventSource.close();
    });
  };

  const handleDateChange = (field, value) => {
    const next = { ...selectedDate, [field]: parseInt(value, 10) };
    const max = daysInMonth(next.year, next.month);
    if (next.day > max) next.day = max;
    setSelectedDate(next);
  };

  const fetchSingleOrder = async (orderId) => {
    if (!orderId || orderId.trim() === '') {
      message.warning('Enter an order ID');
      return;
    }
    try {
      setLoading(true);
      setError(null);
      const response = await fetch(`${config.apiBaseUrl}/api/sale-orders/search/${encodeURIComponent(orderId.trim())}`, {
        headers: getAuthHeaders()
      });
      if (!response.ok) throw new Error('Order not found');
      const data = await response.json();
      if (data.success && data.data) {
        setOrders([data.data]);
        setDashboardStats({ totalOrders: 1, date: `Order ${orderId.trim()}` });
        setHasFetched(true);
        setSearchMode('order');
        setSingleOrderSearch(orderId.trim());
      } else {
        throw new Error(data.message || 'Order not found');
      }
    } catch (err) {
      setError(err.message);
      setOrders([]);
      message.error(`Search failed: ${err.message}`);
    } finally {
      setLoading(false);
      resetView();
    }
  };

  const fetchMultipleOrders = async (orderIds) => {
    if (!orderIds || orderIds.trim() === '') {
      message.warning('Enter at least one order ID');
      return;
    }
    const idList = orderIds.split(/[,\n\s]+/).map((id) => id.trim()).filter(Boolean);
    if (idList.length === 0) {
      message.warning('Enter valid order IDs');
      return;
    }
    try {
      setMultipleOrdersLoading(true);
      setLoading(true);
      setError(null);

      const responses = await Promise.all(
        idList.map((id) =>
          fetch(`${config.apiBaseUrl}/api/sale-orders/search/${encodeURIComponent(id)}`, {
            headers: getAuthHeaders()
          })
            .then((res) => (res.ok ? res.json() : null))
            .catch(() => null)
        )
      );
      const fetchedOrders = responses.filter((res) => res && res.success && res.data).map((res) => res.data);
      if (fetchedOrders.length === 0) throw new Error('No orders found. Check the order IDs.');

      const notFoundIds = idList.filter(
        (id) => !fetchedOrders.some((o) => o.name && o.name.trim().toUpperCase() === id.toUpperCase())
      );

      setOrders(fetchedOrders);
      setDashboardStats({
        totalOrders: fetchedOrders.length,
        date: `${fetchedOrders.length} orders found${notFoundIds.length > 0 ? `, ${notFoundIds.length} not found` : ''}`
      });
      setHasFetched(true);
      setSearchMode('multiple');
      setMultipleOrderSearch(orderIds);

      if (notFoundIds.length > 0) message.warning(`Found ${fetchedOrders.length}. Not found: ${notFoundIds.join(', ')}`);
      else message.success(`Found ${fetchedOrders.length} order(s)`);
    } catch (err) {
      setError(err.message);
      setOrders([]);
      message.error(`Search failed: ${err.message}`);
    } finally {
      setMultipleOrdersLoading(false);
      setLoading(false);
      resetView();
    }
  };

  const handleQuickDate = (daysAgo) => {
    const date = new Date();
    date.setDate(date.getDate() - daysAgo);
    const newDate = { year: date.getFullYear(), month: date.getMonth() + 1, day: date.getDate() };
    setSelectedDate(newDate);
    setSearchMode('date');
    fetchOrders(newDate.year, newDate.month, newDate.day);
  };

  /* ── Filtering / sorting / pagination ── */

  const filteredOrders = orders.filter((order) => {
    const terms = (searchTerm || '').trim().toLowerCase().split(/[\n,]+/).map((t) => t.trim()).filter(Boolean);
    const matchesSearch =
      terms.length === 0 ||
      terms.some(
        (s) =>
          (order.name || '').toString().toLowerCase().includes(s) ||
          (order.customer?.name || '').toString().toLowerCase().includes(s) ||
          (order.customer?.email || '').toString().toLowerCase().includes(s) ||
          String(order.customer?.phone || '').toLowerCase().includes(s) ||
          (order.reference || '').toString().toLowerCase().includes(s) ||
          (getTrackingId(order) || '').toString().toLowerCase().includes(s)
      );
    if (showOnlyWithShipping) return matchesSearch && hasShipping(order);
    if (showOnlyMissingShipping) return matchesSearch && !hasShipping(order);
    return matchesSearch;
  });

  const sortedOrders = !sortConfig.key
    ? filteredOrders
    : [...filteredOrders].sort((a, b) => {
        if (a.name < b.name) return sortConfig.direction === 'asc' ? -1 : 1;
        if (a.name > b.name) return sortConfig.direction === 'asc' ? 1 : -1;
        return 0;
      });

  const handleSort = () =>
    setSortConfig((cur) => ({ key: 'name', direction: cur.key === 'name' && cur.direction === 'asc' ? 'desc' : 'asc' }));

  const handleSelectAll = (e) =>
    setSelectedOrders(e.target.checked ? new Set(sortedOrders.map((o) => o.id)) : new Set());

  const handleSelectOrder = (orderId) => {
    const next = new Set(selectedOrders);
    if (next.has(orderId)) next.delete(orderId);
    else next.add(orderId);
    setSelectedOrders(next);
  };

  const isAllSelected = () => sortedOrders.length > 0 && selectedOrders.size === sortedOrders.length;
  const isIndeterminate = () => selectedOrders.size > 0 && selectedOrders.size < sortedOrders.length;
  const toggleExpandOrder = (orderId) => setExpandedOrder(expandedOrder === orderId ? null : orderId);

  const indexOfLastItem = currentPage * itemsPerPage;
  const currentOrders = sortedOrders.slice(indexOfLastItem - itemsPerPage, indexOfLastItem);
  const totalPages = Math.max(Math.ceil(sortedOrders.length / itemsPerPage), 1);

  const goToPage = (page) => {
    if (page >= 1 && page <= totalPages) setCurrentPage(page);
  };

  const handlePageInputSubmit = (e) => {
    if (e.key === 'Enter') {
      goToPage(parseInt(pageInput, 10));
      setPageInput('');
    }
  };

  const formatDate = (dateString) =>
    new Date(dateString).toLocaleDateString('en-IN', { month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' });

  const getSortIcon = () => {
    if (sortConfig.key !== 'name') return <SwapOutlined className="os-sort-ic" rotate={90} />;
    return sortConfig.direction === 'asc' ? <CaretUpOutlined className="os-sort-ic on" /> : <CaretDownOutlined className="os-sort-ic on" />;
  };

  /* ── Bulk insert ── */

  const handleBulkInsert = async () => {
    if (selectedOrders.size === 0) {
      message.warning('Select at least one order to sync');
      return;
    }
    setInsertLoading(true);
    setInsertProgress(0);
    let progressInterval;

    try {
      const selectedOrdersData = orders.filter((o) => selectedOrders.has(o.id));

      const ordersToInsert = selectedOrdersData.map((order) => {
        const consolidatedDelivery = getConsolidatedDelivery(order.deliveries);
        const products = (consolidatedDelivery?.products || []).map((product) => ({
          id: product.product_id?.toString() || `TEMP-${Date.now()}`,
          updated_id: product.barcode || '',
          sku: product.reference || '',
          name: product.name || '',
          quantity: product.qty || 0,
          weight: 0,
          imageUrl: '',
          productLocation: 'Unknown',
          productCategory: 'Unknown'
        }));

        const shipments = consolidatedDelivery
          ? [{
              courier_name: consolidatedDelivery.carrier || '',
              awb_code: consolidatedDelivery.carrier_tracking_ref || '',
              status: 'PICKUP SCHEDULED',
              attachments: consolidatedDelivery.attachments || []
            }]
          : [];

        return {
          orderID: order.name,
          order_date: new Date(order.date_order),
          customer: {
            name: order.customer?.name || '',
            mobile: order.customer?.phone || '',
            email: order.customer?.email || '',
            tags: order.customer?.tags || [],
            sale_order_count: order.customer?.sale_order_count || 0
          },
          shipments,
          products,
          packed_status: 'Not Completed',
          packed_time: 'Unknown Time',
          packed_person_name: 'Unknown',
          warehouse_out: 'Unknown',
          warehouse_out_time: 'Unknown Time',
          final_package_weight: 0,
          box_type: null,
          packingscheduledat: packingScheduledDate
        };
      });

      progressInterval = setInterval(() => {
        setInsertProgress((prev) => (prev >= 90 ? 90 : prev + 10));
      }, 100);

      const response = await fetch(`${config.apiBaseUrl}/api/orders/bulk`, {
        method: 'POST',
        headers: getAuthHeaders(true),
        body: JSON.stringify({ orders: ordersToInsert })
      });
      clearInterval(progressInterval);
      setInsertProgress(100);

      const result = await response.json();
      if (!response.ok) throw new Error(result.message || 'Failed to sync orders');

      const createdCount = result.data?.created?.length || 0;
      const updatedCount = result.data?.updated?.length || 0;
      const failedCount = result.data?.failed?.length || 0;
      const totalInserted = createdCount + updatedCount;

      let successMsg = `Synced ${totalInserted} orders`;
      if (createdCount > 0) successMsg += ` (${createdCount} new)`;
      if (updatedCount > 0) successMsg += ` (${updatedCount} updated)`;
      if (failedCount > 0) successMsg += ` - ${failedCount} failed`;
      message.success(successMsg);

      try {
        await fetch(`${config.apiBaseUrl}/api/notifications`, {
          method: 'POST',
          headers: getAuthHeaders(true),
          body: JSON.stringify({
            title: 'Orders synced successfully',
            message: `${totalInserted} orders synced to the database (${createdCount} new, ${updatedCount} updated)`,
            type: 'success',
            userId: 'system',
            userName: userName || 'Unknown User',
            data: {
              totalInserted, createdCount, updatedCount, failedCount,
              selectedCount: selectedOrders.size,
              syncDate: new Date().toISOString(),
              syncType: 'bulk_insert'
            }
          })
        });
      } catch (notifError) {
        console.error('Failed to create notification:', notifError);
      }

      setBulkInsertModal(false);
      setSelectedOrders(new Set());
      setPackingScheduledDate(new Date());
    } catch (err) {
      clearInterval(progressInterval);
      console.error('Bulk insert error:', err);
      message.error(`Sync failed: ${err.message}`);
      try {
        await fetch(`${config.apiBaseUrl}/api/notifications`, {
          method: 'POST',
          headers: getAuthHeaders(true),
          body: JSON.stringify({
            title: 'Orders sync failed',
            message: `Error: ${err.message}`,
            type: 'error',
            userId: 'system',
            userName: userName || 'Unknown User',
            data: {
              selectedCount: selectedOrders.size,
              errorMessage: err.message,
              syncDate: new Date().toISOString(),
              syncType: 'bulk_insert'
            }
          })
        });
      } catch (notifError) {
        console.error('Failed to create error notification:', notifError);
      }
    } finally {
      setInsertLoading(false);
      setTimeout(() => setInsertProgress(0), 1000);
    }
  };

  /* ── Exports ── */

  const getSelectedProductsCount = () => {
    if (selectedOrders.size === 0) return 0;
    const ids = new Set();
    orders.filter((o) => selectedOrders.has(o.id)).forEach((order) => {
      getConsolidatedDelivery(order.deliveries)?.products?.forEach((p) => {
        if (p.product_id) ids.add(p.product_id.toString());
      });
    });
    return ids.size;
  };

  const handleExportProducts = async () => {
    if (selectedOrders.size === 0) {
      message.warning('Select at least one order to export products');
      return;
    }
    setExportLoading(true);
    setExportProgress(0);
    let progressInterval;

    try {
      const productMap = new Map();
      orders.filter((o) => selectedOrders.has(o.id)).forEach((order) => {
        getConsolidatedDelivery(order.deliveries)?.products?.forEach((product) => {
          const productId = product.product_id?.toString();
          if (productId && !productMap.has(productId)) {
            productMap.set(productId, {
              productID: product.barcode || '',
              updatedID: product.barcode || '',
              productName: product.name || '',
              sku: product.reference || '',
              productLocation: '',
              productCategory: '',
              imageUrl: ''
            });
          }
        });
      });

      const productsToExport = Array.from(productMap.values());
      if (productsToExport.length === 0) throw new Error('No products found in selected orders');

      progressInterval = setInterval(() => {
        setExportProgress((prev) => (prev >= 90 ? 90 : prev + 10));
      }, 100);

      const headers = ['Product ID', 'Updated ID (Barcode)', 'Product Name', 'SKU', 'Product Location', 'Product Category', 'Image URL'];
      const csvContent = [
        headers.join(','),
        ...productsToExport.map((p) =>
          [
            csvCell(`\t${p.productID}`), // leading tab keeps leading zeros in Excel
            csvCell(`\t${p.updatedID}`),
            csvCell(p.productName),
            csvCell(p.sku),
            csvCell(p.productLocation),
            csvCell(p.productCategory),
            csvCell(p.imageUrl)
          ].join(',')
        )
      ].join('\n');

      const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
      const url = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.setAttribute('href', url);
      link.setAttribute('download', `products_export_${new Date().toISOString().split('T')[0]}.csv`);
      link.style.visibility = 'hidden';
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);

      clearInterval(progressInterval);
      setExportProgress(100);
      message.success(`Exported ${productsToExport.length} products`);
      setExportProductsModal(false);
    } catch (err) {
      clearInterval(progressInterval);
      console.error('Export products error:', err);
      message.error(`Export failed: ${err.message}`);
    } finally {
      setExportLoading(false);
      setTimeout(() => setExportProgress(0), 1000);
    }
  };

  // Exports selected orders when any are selected, otherwise everything currently listed
  const handleExportOrders = () => {
    const source = selectedOrders.size > 0 ? sortedOrders.filter((o) => selectedOrders.has(o.id)) : sortedOrders;
    if (source.length === 0) {
      message.warning('No orders to export');
      return;
    }
    const data = source.map((order) => ({
      'Order ID': order.name,
      Reference: order.reference || '',
      'Customer Name': order.customer?.name || '',
      'Customer Email': order.customer?.email || '',
      'Customer Phone': order.customer?.phone || '',
      Date: new Date(order.date_order).toLocaleDateString(),
      State: order.state,
      Carrier: getCarrierName(order),
      'Tracking Ref': getTrackingId(order),
      'Delivery Status': getDeliveryStatus(order),
      'Sales Team': order.sales_team || ''
    }));
    const ws = XLSX.utils.json_to_sheet(data);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, 'Orders');
    XLSX.writeFile(wb, `orders_export_${selectedDate.year}-${selectedDate.month}-${selectedDate.day}.xlsx`);
    message.success(`Exported ${data.length} orders`);
  };

  /* ── Derived numbers ── */

  const totalProductCount = React.useMemo(() => {
    const ids = new Set();
    orders.forEach((order) => {
      getConsolidatedDelivery(order.deliveries)?.products?.forEach((p) => {
        if (p.product_id) ids.add(p.product_id.toString());
      });
    });
    return ids.size;
  }, [orders]);

  const withShippingCount = filteredOrders.filter(hasShipping).length;
  const missingShippingCount = filteredOrders.length - withShippingCount;

  /* ── Render helpers ── */

  const getStatusBadge = (order) => {
    const c = getConsolidatedDelivery(order.deliveries);
    if (!c) return <span className="odo-status-badge missing">Missing</span>;
    const st = (c.state || '').toLowerCase();
    if (st.includes('cancel')) return <span className="odo-status-badge cancel">{c.state}</span>;
    if (st.includes('done')) return <span className="odo-status-badge done">{c.state}</span>;
    if (st.includes('assigned') || st.includes('waiting')) return <span className="odo-status-badge shipped">{c.state}</span>;
    return <span className="odo-status-badge pending">{c.state || 'Pending'}</span>;
  };

  const renderExpandedRow = (order) => {
    const c = getConsolidatedDelivery(order.deliveries);
    return (
      <tr className="odo-expanded-row">
        <td colSpan="8">
          <div className="odo-expanded-inner">
            <div className="odo-expanded-section">
              <h4><UserOutlined /> Customer</h4>
              <div className="info-row"><span className="info-label">Name</span><span className="info-value">{order.customer?.name || '-'}</span></div>
              <div className="info-row"><span className="info-label">Email</span><span className="info-value">{order.customer?.email || '-'}</span></div>
              <div className="info-row"><span className="info-label">Phone</span><span className="info-value">{order.customer?.phone || '-'}</span></div>
              <div className="info-row"><span className="info-label">Tags</span><span className="info-value">{(order.customer?.tags || []).join(', ') || '-'}</span></div>
              <div className="info-row"><span className="info-label">Salesperson</span><span className="info-value">{order.salesperson || '-'}</span></div>
            </div>
            <div className="odo-expanded-section">
              <h4><CarOutlined /> Shipping</h4>
              <div className="info-row"><span className="info-label">Carrier</span><span className="info-value">{c?.carrier || '-'}</span></div>
              <div className="info-row"><span className="info-label">Tracking</span><span className="info-value">{c?.carrier_tracking_ref || '-'}</span></div>
              <div className="info-row"><span className="info-label">Status</span><span className="info-value">{c?.state || '-'}</span></div>
              <div className="info-row"><span className="info-label">Delivery</span><span className="info-value">{c?.name || '-'}</span></div>
            </div>
            <div className="odo-expanded-section">
              <h4><FileTextOutlined /> Order</h4>
              <div className="info-row"><span className="info-label">Amount</span><span className="info-value">₹{order.amount_total?.toLocaleString() || '-'}</span></div>
              <div className="info-row"><span className="info-label">Date</span><span className="info-value">{order.date_order ? formatDate(order.date_order) : '-'}</span></div>
              <div className="info-row"><span className="info-label">Reference</span><span className="info-value">{order.reference || '-'}</span></div>
              <div className="info-row"><span className="info-label">Sales team</span><span className="info-value">{order.sales_team || '-'}</span></div>
            </div>
            {c && c.products && c.products.length > 0 ? (
              <div className="odo-expanded-products">
                <h4><InboxOutlined /> Products ({c.products.length})</h4>
                <div className="odo-products-grid">
                  {c.products.map((p, i) => (
                    <div key={i} className="odo-product-chip">
                      <div>
                        <div className="prod-name" title={p.name}>{p.name}</div>
                        <div className="prod-detail">Barcode {p.barcode || '-'} · Ref {p.reference || '-'} · Qty {p.qty} {p.uom}</div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            ) : (
              <div className="odo-expanded-products"><div className="odo-no-data">No WH/OUT deliveries or products found</div></div>
            )}
          </div>
        </td>
      </tr>
    );
  };

  const fetchPct = fetchProgress.totalBatches > 0
    ? Math.min(Math.round((fetchProgress.currentBatch / fetchProgress.totalBatches) * 100), 100)
    : 0;

  const isYesterday = (() => {
    const y = getYesterdayDate();
    return selectedDate.year === y.year && selectedDate.month === y.month && selectedDate.day === y.day;
  })();
  const now = new Date();
  const isToday = selectedDate.year === now.getFullYear() && selectedDate.month === now.getMonth() + 1 && selectedDate.day === now.getDate();

  /* ── JSX ── */

  return (
    <div className={`odoosync-container ${darkTheme ? 'dark-mode' : 'light-mode'}`}>
      {/* Header */}
      <header className="os-header">
        <div className="os-header-left">
          <span className="os-header-icon"><SyncOutlined /></span>
          <div>
            <h1 className="os-header-title">Odoo order sync</h1>
            <p className="os-header-sub">Bring Odoo orders into the Packing Console</p>
          </div>
        </div>
        <div className="os-header-right">
          <button className="os-btn" onClick={() => fetchOrders(selectedDate.year, selectedDate.month, selectedDate.day)} disabled={loading || searchMode !== 'date'}>
            <ReloadOutlined /> Refresh
          </button>
          <button className="os-btn" onClick={handleExportOrders} disabled={!hasFetched || sortedOrders.length === 0}>
            <ExportOutlined /> Export
          </button>
        </div>
      </header>

      <main className="os-page">
        {/* Find orders */}
        <section className="os-controls">
          <div className="os-controls-row">
            <div className="os-mode-tabs" role="tablist">
              <button role="tab" aria-selected={searchMode === 'date'} className={`os-mode-tab ${searchMode === 'date' ? 'active' : ''}`}
                onClick={() => { setSearchMode('date'); setSingleOrderSearch(''); setMultipleOrderSearch(''); setOrders([]); setHasFetched(false); }}>
                <CalendarOutlined /> By date
              </button>
              <button role="tab" aria-selected={searchMode === 'order'} className={`os-mode-tab ${searchMode === 'order' ? 'active' : ''}`} onClick={() => setSearchMode('order')}>
                <SearchOutlined /> Single order
              </button>
              <button role="tab" aria-selected={searchMode === 'multiple'} className={`os-mode-tab ${searchMode === 'multiple' ? 'active' : ''}`} onClick={() => setSearchMode('multiple')}>
                <UnorderedListOutlined /> Multiple orders
              </button>
            </div>

            {searchMode === 'date' && (
              <div className="os-controls-inline odo-fade-in">
                <select className="os-select" aria-label="Year" value={selectedDate.year} onChange={(e) => handleDateChange('year', e.target.value)}>
                  {[...Array(5)].map((_, i) => { const y = new Date().getFullYear() - i; return <option key={y} value={y}>{y}</option>; })}
                </select>
                <select className="os-select" aria-label="Month" value={selectedDate.month} onChange={(e) => handleDateChange('month', e.target.value)}>
                  {[...Array(12)].map((_, i) => <option key={i + 1} value={i + 1}>{new Date(2000, i).toLocaleString('en', { month: 'short' })}</option>)}
                </select>
                <select className="os-select" aria-label="Day" value={selectedDate.day} onChange={(e) => handleDateChange('day', e.target.value)}>
                  {[...Array(daysInMonth(selectedDate.year, selectedDate.month))].map((_, i) => <option key={i + 1} value={i + 1}>{i + 1}</option>)}
                </select>
                <button className="os-btn primary" onClick={() => fetchOrders(selectedDate.year, selectedDate.month, selectedDate.day)} disabled={loading}>
                  {loading ? <LoadingOutlined /> : <DatabaseOutlined />} {loading ? 'Loading…' : 'Load orders'}
                </button>
                <span className="os-divider" />
                <button className={`os-btn quicky ${isYesterday ? 'active' : ''}`} onClick={() => handleQuickDate(1)}>Yesterday</button>
                <button className={`os-btn quicky ${isToday ? 'active' : ''}`} onClick={() => handleQuickDate(0)}>Today</button>
              </div>
            )}

            {searchMode === 'order' && (
              <div className="os-controls-inline odo-fade-in">
                <input className="os-input" placeholder="Order ID, e.g. SO208223" value={singleOrderSearch}
                  onChange={(e) => setSingleOrderSearch(e.target.value)}
                  onKeyDown={(e) => { if (e.key === 'Enter' && singleOrderSearch.trim()) fetchSingleOrder(singleOrderSearch); }}
                  disabled={loading} />
                <button className="os-btn primary" onClick={() => fetchSingleOrder(singleOrderSearch)} disabled={loading || !singleOrderSearch.trim()}>
                  {loading ? <LoadingOutlined /> : <SearchOutlined />} {loading ? 'Searching…' : 'Search'}
                </button>
              </div>
            )}

            {searchMode === 'multiple' && (
              <div className="os-controls-inline odo-fade-in">
                <textarea className="os-textarea" rows={2} placeholder="Order IDs separated by commas, spaces or new lines" value={multipleOrderSearch}
                  onChange={(e) => setMultipleOrderSearch(e.target.value)}
                  onKeyDown={(e) => { if (e.ctrlKey && e.key === 'Enter' && multipleOrderSearch.trim()) fetchMultipleOrders(multipleOrderSearch); }}
                  disabled={multipleOrdersLoading} />
                <button className="os-btn primary" onClick={() => fetchMultipleOrders(multipleOrderSearch)} disabled={multipleOrdersLoading || !multipleOrderSearch.trim()}>
                  {multipleOrdersLoading ? <LoadingOutlined /> : <SearchOutlined />} {multipleOrdersLoading ? 'Searching…' : 'Search'}
                </button>
              </div>
            )}
          </div>

          {hasFetched && (
            <div className="os-filters">
              <label className={`os-chip ${showOnlyWithShipping ? 'active' : ''}`}>
                <input type="checkbox" checked={showOnlyWithShipping} onChange={(e) => { setShowOnlyWithShipping(e.target.checked); setShowOnlyMissingShipping(false); setCurrentPage(1); }} />
                <CarOutlined /> With shipping
              </label>
              <label className={`os-chip warn ${showOnlyMissingShipping ? 'active' : ''}`}>
                <input type="checkbox" checked={showOnlyMissingShipping} onChange={(e) => { setShowOnlyMissingShipping(e.target.checked); setShowOnlyWithShipping(false); setCurrentPage(1); }} />
                <WarningOutlined /> Missing shipping
              </label>
            </div>
          )}
        </section>

        {error && <div className="os-error odo-fade-in" role="alert"><CloseCircleFilled /> <span>{error}</span></div>}

        {/* KPIs */}
        {hasFetched && (
          <section className="os-kpi odo-fade-in" aria-label="Summary">
            <div className="os-kpi-card"><div className="os-kpi-icon green"><ShoppingOutlined /></div><div className="os-kpi-num">{dashboardStats.totalOrders}</div><div className="os-kpi-lbl">Total orders</div></div>
            <div className="os-kpi-card"><div className="os-kpi-icon amber"><CheckSquareOutlined /></div><div className="os-kpi-num">{selectedOrders.size}</div><div className="os-kpi-lbl">Selected</div></div>
            <div className="os-kpi-card"><div className="os-kpi-icon blue"><AppstoreOutlined /></div><div className="os-kpi-num">{totalProductCount}</div><div className="os-kpi-lbl">Unique products</div></div>
            <div className="os-kpi-card"><div className="os-kpi-icon teal"><CarOutlined /></div><div className="os-kpi-num">{withShippingCount}</div><div className="os-kpi-lbl">With shipping</div></div>
            <div className="os-kpi-card"><div className="os-kpi-icon red"><ExclamationCircleOutlined /></div><div className="os-kpi-num">{missingShippingCount}</div><div className="os-kpi-lbl">Missing shipping</div></div>
          </section>
        )}

        {/* Orders */}
        {hasFetched && (
          <section className="os-orders odo-fade-in">
            <div className="os-orders-head">
              <div className="os-orders-title">
                <h3>Orders · {dashboardStats.date || `${selectedDate.year}-${selectedDate.month}-${selectedDate.day}`}</h3>
                <span className="os-orders-count">{sortedOrders.length} shown</span>
              </div>
              <div className="os-orders-actions">
                <div className="os-search">
                  <SearchOutlined />
                  <input className="os-input sm" placeholder="Search order, customer, phone, AWB" value={searchTerm}
                    onChange={(e) => { setSearchTerm(e.target.value); setCurrentPage(1); }} />
                </div>
                <select className="os-select sm" aria-label="Rows per page" value={itemsPerPage} onChange={(e) => { setItemsPerPage(Number(e.target.value)); setCurrentPage(1); }}>
                  {[25, 50, 100].map((n) => <option key={n} value={n}>{n} per page</option>)}
                </select>
              </div>
            </div>

            <div className="os-table-wrap">
              <table className="os-table">
                <thead>
                  <tr>
                    <th className="os-th-chk">
                      <input type="checkbox" aria-label="Select all orders" checked={isAllSelected()} ref={(el) => { if (el) el.indeterminate = isIndeterminate(); }} onChange={handleSelectAll} />
                    </th>
                    <th className="sortable" onClick={handleSort}>Order ID {getSortIcon()}</th>
                    <th>Customer</th><th>Date</th><th>Courier</th><th>Tracking / AWB</th><th>Status</th>
                    <th className="os-th-act"><span className="sr-only">Details</span></th>
                  </tr>
                </thead>
                <tbody>
                  {currentOrders.length === 0 ? (
                    <tr><td colSpan="8" className="os-no-data">No orders match the current search or filters.</td></tr>
                  ) : currentOrders.map((order) => {
                    const isExpanded = expandedOrder === order.id;
                    const tracking = getTrackingId(order);
                    return (
                      <React.Fragment key={order.id}>
                        <tr className={selectedOrders.has(order.id) ? 'os-row-selected' : ''}>
                          <td className="os-td-chk"><input type="checkbox" aria-label={`Select ${order.name}`} checked={selectedOrders.has(order.id)} onChange={() => handleSelectOrder(order.id)} /></td>
                          <td className="os-td-id"><span className="os-order-id">{order.name}</span></td>
                          <td className="os-td-cust" title={order.customer?.name}>{order.customer?.name || '-'}</td>
                          <td className="os-td-date">{order.date_order ? formatDate(order.date_order) : '-'}</td>
                          <td>{getCarrierName(order)}</td>
                          <td className="os-td-track">{tracking !== '-' ? tracking : <span className="os-missing">Missing</span>}</td>
                          <td>{getStatusBadge(order)}</td>
                          <td className="os-td-act">
                            <button className={`os-expand ${isExpanded ? 'open' : ''}`} aria-label={isExpanded ? 'Hide details' : 'Show details'} aria-expanded={isExpanded} onClick={() => toggleExpandOrder(order.id)}>
                              {isExpanded ? <DownOutlined /> : <RightOutlined />}
                            </button>
                          </td>
                        </tr>
                        {isExpanded && renderExpandedRow(order)}
                      </React.Fragment>
                    );
                  })}
                </tbody>
              </table>
            </div>

            {sortedOrders.length > 0 && (
              <div className="os-pagination">
                <span className="os-page-info">Page {currentPage} of {totalPages}</span>
                <div className="os-page-btns">
                  <button className="os-page-btn" aria-label="First page" onClick={() => goToPage(1)} disabled={currentPage === 1}><DoubleLeftOutlined /></button>
                  <button className="os-page-btn" aria-label="Previous page" onClick={() => goToPage(currentPage - 1)} disabled={currentPage === 1}><LeftOutlined /></button>
                  <input className="os-page-input" type="number" aria-label="Go to page" value={pageInput} onChange={(e) => setPageInput(e.target.value)} onKeyDown={handlePageInputSubmit} placeholder={String(currentPage)} min="1" max={totalPages} />
                  <button className="os-page-btn" aria-label="Next page" onClick={() => goToPage(currentPage + 1)} disabled={currentPage === totalPages}><RightOutlined /></button>
                  <button className="os-page-btn" aria-label="Last page" onClick={() => goToPage(totalPages)} disabled={currentPage === totalPages}><DoubleRightOutlined /></button>
                </div>
              </div>
            )}

            {selectedOrders.size > 0 && (
              <div className="os-action-bar">
                <div className="os-action-info">
                  <span><CheckCircleOutlined /> {selectedOrders.size} orders selected</span>
                  <span><InboxOutlined /> {getSelectedProductsCount()} products</span>
                  <button className="os-clear" onClick={() => setSelectedOrders(new Set())}><CloseOutlined /> Clear</button>
                </div>
                <div className="os-action-btns">
                  <button className="os-btn" onClick={() => setExportProductsModal(true)}><DownloadOutlined /> Products CSV</button>
                  <button className="os-btn" onClick={handleExportOrders}><FileExcelOutlined /> Orders Excel</button>
                  <button className="os-btn primary" onClick={() => setBulkInsertModal(true)}><CloudUploadOutlined /> Sync selected</button>
                </div>
              </div>
            )}
          </section>
        )}

        {!hasFetched && !loading && (
          <section className="os-welcome odo-fade-in">
            <div className="os-welcome-icon"><DatabaseOutlined /></div>
            <h2>No orders loaded yet</h2>
            <p>Pick a date and choose Load orders, or search by order ID.</p>
          </section>
        )}
      </main>

      {/* Fetch progress */}
      <Modal open={loading && fetchProgress.status !== 'idle' && searchMode === 'date'} footer={null} closable={false} centered width={480} className="os-fetch-modal" getContainer={false}>
        <div className="os-fetch-content">
          <div className="os-fetch-title"><SyncOutlined spin /> Fetching orders from Odoo</div>
          <p className="os-fetch-sub">This can take a minute for busy days.</p>
          <div className="os-fetch-bar-wrap">
            <div className="os-fetch-bar-track"><div className="os-fetch-bar-fill" style={{ width: fetchProgress.totalBatches > 0 ? `${fetchPct}%` : '10%' }} /></div>
            <span className="os-fetch-pct">{fetchProgress.totalBatches > 0 ? `${fetchPct}%` : '…'}</span>
          </div>
          <div className="os-fetch-batch-label">
            {fetchProgress.totalBatches > 0 ? `Batch ${Math.min(fetchProgress.currentBatch + 1, fetchProgress.totalBatches)} of ${fetchProgress.totalBatches}` : 'Connecting…'}
          </div>
          <div className="os-fetch-stats">
            <div className="os-fetch-stat"><span className="os-fetch-stat-val">{fetchProgress.currentBatch || 0}</span><span className="os-fetch-stat-lbl">Completed</span></div>
            <div className="os-fetch-stat"><span className="os-fetch-stat-val">{Math.max((fetchProgress.totalBatches || 0) - (fetchProgress.currentBatch || 0), 0)}</span><span className="os-fetch-stat-lbl">Remaining</span></div>
            <div className="os-fetch-stat"><span className="os-fetch-stat-val">{(fetchProgress.ordersFetched || 0).toLocaleString()}</span><span className="os-fetch-stat-lbl">Orders fetched</span></div>
          </div>
          {fetchProgress.activities.length > 0 && (
            <>
              <div className="os-fetch-log-label">Activity</div>
              <div className="os-fetch-log" ref={(el) => { if (el) el.scrollTop = el.scrollHeight; }}>
                {fetchProgress.activities.map((act, i) => (
                  <div className="os-fetch-log-item" key={i}>
                    <span className="os-fetch-log-time">{act.timestamp}</span>
                    <span className={`os-fetch-log-icon ${act.step === 'batch_complete' ? 'done' : 'spin'}`}>
                      {act.step === 'batch_complete' ? <CheckCircleFilled /> : <SyncOutlined />}
                    </span>
                    <span className="os-fetch-log-msg">{act.message}</span>
                  </div>
                ))}
              </div>
            </>
          )}
          {fetchProgress.status === 'complete' && (
            <div className="os-fetch-done"><CheckCircleFilled /> Fetch complete · {fetchProgress.totalOrders?.toLocaleString()} orders</div>
          )}
        </div>
      </Modal>

      {/* Sync modal */}
      <Modal
        title="Sync orders to Packing Console"
        open={bulkInsertModal}
        onCancel={() => !insertLoading && setBulkInsertModal(false)}
        getContainer={false}
        footer={[
          <button key="c" className="os-btn" onClick={() => setBulkInsertModal(false)} disabled={insertLoading}>Cancel</button>,
          <button key="i" className="os-btn primary" onClick={handleBulkInsert} disabled={insertLoading}>
            {insertLoading ? <LoadingOutlined /> : <CloudUploadOutlined />} {insertLoading ? 'Syncing…' : `Sync ${selectedOrders.size} orders`}
          </button>
        ]}
        width={500}
        className="os-modal"
      >
        <div className="os-modal-body">
          <p>Sync <strong>{selectedOrders.size}</strong> selected orders to the database.</p>
          <div className="os-modal-field">
            <label><CalendarOutlined /> Packing scheduled date</label>
            <Calendar value={packingScheduledDate} onChange={(e) => setPackingScheduledDate(e.value)} dateFormat="dd/mm/yy" showIcon style={{ width: '100%' }} />
          </div>
          {insertLoading && <Progress percent={insertProgress} status="active" strokeColor="#0f766e" />}
        </div>
      </Modal>

      {/* Export products modal */}
      <Modal
        title="Export products"
        open={exportProductsModal}
        onCancel={() => !exportLoading && setExportProductsModal(false)}
        getContainer={false}
        footer={[
          <button key="c" className="os-btn" onClick={() => setExportProductsModal(false)} disabled={exportLoading}>Cancel</button>,
          <button key="e" className="os-btn primary" onClick={handleExportProducts} disabled={exportLoading}>
            {exportLoading ? <LoadingOutlined /> : <DownloadOutlined />} {exportLoading ? 'Exporting…' : `Export ${getSelectedProductsCount()} products`}
          </button>
        ]}
        width={500}
        className="os-modal"
      >
        <div className="os-modal-body">
          <p>Export <strong>{getSelectedProductsCount()}</strong> unique products from <strong>{selectedOrders.size}</strong> orders as a CSV file.</p>
          {exportLoading && <Progress percent={exportProgress} status="active" strokeColor="#0f766e" />}
        </div>
      </Modal>
    </div>
  );
}

export default OdooSync;
